import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ShieldCheck, Lock, FileSearch, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useBranding } from "@/lib/branding";
import { Ambient, BrandMark } from "@/components/layout/Ambient";
import { useTheme } from "@/lib/theme";
import { PasswordInput } from "@/components/auth/PasswordInput";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function Login() {
  const { user, loading, login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const b = useBranding();
  useTheme();
  useEffect(() => { document.title = `Sign in · ${b.name}`; }, [b.name]);
  if (!loading && user) return <Navigate to="/dashboard" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/auth/login", { email: f.email, password: f.password });
      await login(r.data);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      if (d?.locked_until) setErr(`Too many failed attempts. Try again after ${fmtTime(d.locked_until)}.`);
      else if (e2.response?.status === 401) setErr("Email or password is incorrect");
      else setErr(typeof d === "string" ? d : "Sign-in failed. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="relative min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <Ambient />
      <div className="relative z-10 hidden lg:flex flex-col justify-between bg-[#050C22] p-12 overflow-hidden border-r border-amber-300/10">
        <img src="/archive.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#040A1C] via-[#071340]/80 to-[#0A1B4D]/40" />
        <div className="relative flex items-center gap-3"><BrandMark size="w-12 h-12" testid="login-brand-mark" />
          <div><div className="font-display font-bold text-white text-lg" data-testid="login-org-name">{b.name}</div><div className="text-[10px] uppercase tracking-[0.3em] text-amber-200/80">{b.tagline}</div></div></div>
        <div className="relative max-w-lg stagger">
          <h1 className="text-4xl xl:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">Every official record. <span className="text-gold">Numbered, approved, accounted for.</span></h1>
          <p className="mt-5 text-slate-300 text-base leading-relaxed">Correspondence, minutes, PO4 records, Fingers Reports and FC Pronouncements — under strict roles, locked approvals and an immutable audit trail.</p>
          <div className="mt-8 grid grid-cols-3 gap-4 text-slate-300 text-xs">
            <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-sky-300" />Locked approvals</div>
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sky-300" />Hash-chained audit</div>
            <div className="flex items-center gap-2"><FileSearch className="w-4 h-4 text-sky-300" />Fast retrieval</div>
          </div>
        </div>
        <div className="relative text-xs text-slate-500">Internal use only. Access by invitation.</div>
      </div>
      <div className="relative z-10 flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up rounded-2xl glass border glow-border p-8" data-testid="login-form">
          <div className="lg:hidden mb-6 flex items-center gap-3"><BrandMark size="w-12 h-12" />
            <div><div className="font-display font-bold text-slate-900 dark:!text-white">{b.name}</div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 dark:!text-amber-200/80">{b.tagline}</div></div></div>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:!text-white">Sign in</h2>
          <p className="mt-2 text-sm text-slate-600 dark:!text-slate-300">Use the email address you were invited with.</p>
          <div className="mt-6 space-y-3">
            <label className="block text-xs font-semibold text-slate-600 dark:!text-slate-300">Email
              <Input type="email" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-white h-12 text-base mt-1" data-testid="login-email" /></label>
            <label className="block text-xs font-semibold text-slate-600 dark:!text-slate-300">Password
              <div className="mt-1"><PasswordInput value={f.password} onChange={(v) => setF({ ...f, password: v })} testid="login-password" /></div></label>
          </div>
          {err && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="login-error">{err}</div>}
          <Button type="submit" disabled={busy || !f.email || !f.password} className="mt-6 w-full h-12 rounded-full text-base" data-testid="login-submit">
            <LogIn className="w-4 h-4 mr-2" />{busy ? "Signing in…" : "Sign in"}
          </Button>
          <p className="mt-5 text-xs text-slate-500 dark:!text-slate-400" data-testid="login-forgot-note">Forgot your password? Ask the Scribe to reset it for you.</p>
        </form>
      </div>
    </div>
  );
}
